import React, { useMemo, useRef } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { type OptionProps, type OptionType, CheckboxOption, PopupSelect } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { type MessageDescriptorV2, useIntl } from '@atlassian/jira-intl';
import type {
	DevOpsPullRequestStatus,
	statusFilter_softwareCodePage$data,
	statusFilter_softwareCodePage$key,
} from '@atlassian/jira-relay/src/__generated__/statusFilter_softwareCodePage.graphql';
import { STATUS_FILTER_BUTTON_TEST_ID } from '../../../../../../../common/constants';
import { FilterButton } from '../../../../../../../common/ui/filter-button';
import PRStatusLozenge from '../../../../../../../common/ui/pr-status-lozenge';
import lozengeMessages from '../../../../../../../common/ui/pr-status-lozenge/messages';
import { usePullRequestTableOptionsStore } from '../../../../../../../controllers/pull-request-filters';
import messages from './messages';

type Extends<T, U extends T> = U;
type BasePullRequestStatusOptions = Extends<
	DevOpsPullRequestStatus,
	'OPEN' | 'MERGED' | 'DECLINED'
>;

const BaseSelectOptions: Record<
	BasePullRequestStatusOptions,
	{ message: MessageDescriptorV2; value: DevOpsPullRequestStatus }
> = {
	OPEN: {
		message: lozengeMessages.open,
		value: 'OPEN',
	},
	MERGED: {
		message: lozengeMessages.merged,
		value: 'MERGED',
	},
	DECLINED: {
		message: lozengeMessages.declined,
		value: 'DECLINED',
	},
};

const UnknownSelectOption: { message: MessageDescriptorV2; value: 'UNKNOWN' } = {
	message: lozengeMessages.unknown,
	value: 'UNKNOWN',
};

const AllSelectOptions = { ...BaseSelectOptions, UNKNOWN: UnknownSelectOption };

type GetSelectOptionsArgs = {
	statusList: statusFilter_softwareCodePage$data;
	statusFilter: string[];
	formatMessage: (MessageDescriptorV2: MessageDescriptorV2) => string;
};

export const getSelectOptions = ({
	statusList,
	statusFilter,
	formatMessage,
}: GetSelectOptionsArgs): OptionType[] =>
	Object.values(
		// Add 'Unknown' only if there are PRs in the list with that status, or if it's in the query param
		statusList.some(({ status }) => status === UnknownSelectOption.value) ||
			statusFilter.includes(UnknownSelectOption.value)
			? AllSelectOptions
			: // By default we add 'Open', 'Merged' and 'Closed' as select options
				BaseSelectOptions,
	).map(({ message, value }) => ({
		label: formatMessage(message),
		value,
	}));

const getSelectDefaults = (
	data: statusFilter_softwareCodePage$data,
	statusFilter: string[],
	formatMessage: (MessageDescriptorV2: MessageDescriptorV2) => string,
) => ({
	options: getSelectOptions({ statusList: data, statusFilter, formatMessage }),
	placeholder: formatMessage(messages.statusLabel),
	'aria-label': formatMessage(messages.statusLabel),
	components: {
		Option: ({ children, data: checkboxData, ...props }: OptionProps<OptionType, true>) => (
			<CheckboxOption data={checkboxData} {...props}>
				<LozengeWrapper>
					{/* @ts-expect-error - 2322 - Type 'string | number' is not assignable to type 'DevOpsPullRequestStatus | null'. Type 'string' is not assignable to type 'DevOpsPullRequestStatus | null' */}
					<PRStatusLozenge status={checkboxData.value} />
				</LozengeWrapper>
			</CheckboxOption>
		),
	},
});

type StatusFilterProps = {
	disabled: boolean;
	statusFilterData: statusFilter_softwareCodePage$key;
};

const StatusFilter = ({ disabled, statusFilterData }: StatusFilterProps) => {
	const data = useFragment(
		graphql`
			fragment statusFilter_softwareCodePage on DevOpsPullRequestDetails @relay(plural: true) {
				status
			}
		`,
		statusFilterData,
	);

	const { formatMessage } = useIntl();

	const [{ statusFilter }, filtersActions] = usePullRequestTableOptionsStore();

	// Preserving initialFilters to not break the `defaults` memo by passing `statusFilter` which changes whenever an option is selected.
	const initialFilters = useRef<string[]>(statusFilter);

	const defaults = useMemo(
		() => getSelectDefaults(data, initialFilters.current, formatMessage),
		[data, formatMessage],
	);

	const selectedValues = useMemo(
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		() => defaults.options.filter((option) => statusFilter.includes(option.value as string)),
		[statusFilter, defaults.options],
	);

	return (
		<PopupSelect
			{...defaults}
			value={selectedValues}
			backspaceRemovesValue
			controlShouldRenderValue
			isClearable
			closeMenuOnSelect={false}
			hideSelectedOptions={false}
			isDisabled={disabled}
			classNamePrefix="status-filter"
			// Temporary strategy to mitigate a PopupSelect regression causing scroll jumps
			popperProps={{ strategy: 'fixed' }}
			isMulti
			onChange={(selectedOptions) =>
				filtersActions.setStatusFilter(
					selectedOptions
						.map((option) => option.value)
						.filter((value): value is string => typeof value === 'string'),
				)
			}
			target={({ isOpen, ...triggerProps }) => (
				<FilterButton
					{...triggerProps}
					testId={STATUS_FILTER_BUTTON_TEST_ID}
					isSelected={isOpen}
					label={formatMessage(messages.statusLabel)}
					selectedCount={statusFilter.length}
				/>
			)}
		/>
	);
};

export default StatusFilter;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	marginLeft: token('space.050', '4px'),
	padding: `${token('space.075', '6px')} 0`,
});
